import { alpha, Theme } from '@mui/material/styles'
//
import {
  SEGMENT_COLORS,
  SegmentColor,
  SEGMENTED_THUMB_CLASS,
} from 'src/components/SegmentedControl'
import { STRIP_FOCUS } from '../hover'
import { ColorSchema } from '../palette'
import {
  layout,
  semanticTokens,
  typography as typeTokens,
} from '../tokens.generated'
import { FOCUS_VISIBLE, focusRingShadow } from './Button'

// ----------------------------------------------------------------------
// A segmented control is a control, so its outside edge lands on the same
// three steps as a button and an input. It had no height at all: the group
// came to whatever its cells' padding, their 4px margins and its own hairline
// added up to, which at `size="small"` was 46px: on no step, and 14px taller
// than the select beside it on the reports screen.
//
// The step is given to the *cell*, minus what the group adds around it, so the
// number that lands on the ramp is the thing a person sees the edge of.
//
// It is one control and has to read as one: a track, the sunken surface the
// token source names for it, with the options written on it, and a thumb
// under the selected one that travels to the option pressed. Inside a group a
// cell paints no fill of its own, selected or not. A fill per cell, swapped in
// place, is what made it read as buttons in a box, and it is also a fill that
// cannot travel. `SegmentedControl` places the thumb; what it looks like is
// decided here. A toggle on its own, with no group and so no thumb (the
// calendar's view buttons), still paints its selection itself.
//
// The label is 13px, body2's size, at every step. Every control started at
// `small`, where MUI sets 13px, and moved to the 40px step because a 22px cell
// was too tight around its word. A taller step is room around the label and
// the inset of its own step beside it, never a bigger word.
// ----------------------------------------------------------------------

/** The gap the group keeps between a cell and its own edge. */
const CELL_INSET = 4

/** The group's hairline, top and bottom. */
const HAIRLINE = 1

/** The cell height that makes the group itself `step` tall. */
const cellHeight = (step: number) => step - 2 * (CELL_INSET + HAIRLINE)

/**
 * A selected cell, by the colour it was asked for: the fill of the thumb under
 * it, the ink of its label, and the ink the label steps to under the pointer.
 *
 * A cell asked for by a semantic's name takes that semantic's soft pair, the
 * way the standard cell takes the accent's. MUI's own selected cell is the
 * main colour as text over a 8% wash, which for `warning` is yellow ink on
 * near white. The Speeding tab's Dismissed was the first cell to ask for one,
 * and since 2026-11-01 asks for the attention orange rather than the yellow.
 * `primary` and `secondary` are not semantics and keep MUI's pair.
 */
export const selectedLook = (theme: Theme, color: SegmentColor) => {
  if (color === 'standard') {
    return {
      fill: theme.palette.primary.lighter,
      ink: theme.palette.primary.dark,
      hoverInk: theme.hover.selectedInk,
    }
  }
  const semantics = semanticTokens[theme.palette.mode]
  if (color in semantics) {
    const roles = semantics[color as keyof typeof semantics]
    return { fill: roles.soft, ink: roles.text, hoverInk: roles.text }
  }
  const { main } = theme.palette[color as ColorSchema]
  return {
    fill: alpha(main, theme.palette.action.selectedOpacity),
    ink: main,
    hoverInk: main,
  }
}

// ----------------------------------------------------------------------

export default function ToggleButton(theme: Theme) {
  /**
   * The selection a cell paints itself, which is all a toggle on its own has.
   * `primary` and `secondary` leave MUI's own in place, as they always have.
   */
  const selected = (color: SegmentColor) => {
    if (
      color !== 'standard' &&
      !(color in semanticTokens[theme.palette.mode])
    ) {
      return {}
    }
    const look = selectedLook(theme, color)
    return {
      'color': look.ink,
      'backgroundColor': look.fill,
      '&:hover': { backgroundColor: look.fill, color: look.hoverInk },
    }
  }

  const style = (color: ColorSchema | 'attention') => ({
    props: { color },
    style: {
      '&:hover': {
        borderColor: alpha(theme.palette[color].main, 0.48),
        backgroundColor: alpha(
          theme.palette[color].main,
          theme.palette.action.hoverOpacity,
        ),
      },
      '&.Mui-selected': {
        borderColor: alpha(theme.palette[color].main, 0.48),
        ...selected(color),
      },
    },
  })

  return {
    MuiToggleButton: {
      defaultProps: {
        // A toggle answers a press by becoming the selected one: the thumb
        // arriving, or on a toggle on its own its fill. A ripple is a second
        // answer, a darker blot spreading over the thumb as it lands, and the
        // one a button gives rather than a switch. The tab strip, the other
        // selection that travels, has none either.
        disableRipple: true,
      },
      styleOverrides: {
        // The same ring every other control takes, on a toggle on its own
        // (the calendar's view buttons): it has an edge of its own, and often
        // no words an underline could stand under. A cell in a segmented
        // control answers with the underline instead, below.
        root: {
          [FOCUS_VISIBLE]: {
            outline: 'none',
            boxShadow: focusRingShadow(theme),
          },
        },
      },
      variants: [
        {
          // A segmented cell is a surface, and an unselected one shipped with
          // no answer to the pointer at all. The selected cell answers with
          // its ink, because its fill, or the thumb under it, is already
          // stating the selection.
          props: { color: 'standard' },
          style: {
            'transition': theme.hover.transition,
            '&:not(.Mui-selected):hover': {
              backgroundColor: theme.hover.surface,
              color: theme.hover.ink,
            },
            // The pointer's answer, for the keyboard too.
            '&:not(.Mui-selected):focus-visible, &:not(.Mui-selected).Mui-focusVisible':
              {
                backgroundColor: theme.hover.surface,
                color: theme.hover.ink,
              },
            '&.Mui-selected': selected('standard'),
          },
        },
        style('primary'),
        style('secondary'),
        style('info'),
        style('success'),
        style('warning'),
        style('error'),
        style('attention'),
      ],
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          // The thumb's containing block, and the offset parent of the cells
          // `SegmentedControl` measures it against.
          'position': 'relative',
          // The stacking below stays inside the group, so a label's z-index
          // never lifts it over a sticky header the page scrolls under.
          'isolation': 'isolate',
          'borderRadius': theme.shape.borderRadius,
          'backgroundColor': theme.palette.background.neutral,
          'border': `solid 1px ${theme.palette.grey[500_12]}`,
          // As wide as its segments. A group is a flex item wherever it sits,
          // and stretched by a column it drew track past its last segment.
          '&:not(.MuiToggleButtonGroup-fullWidth)': {
            width: 'fit-content',
          },
          // And never wider than the room it is given. Where its segments
          // need more, they give up inset rather than push the page sideways:
          // the cells shrink, and a label centred in a shrinking cell keeps
          // its word whole. Costs' month switcher was 380px on a phone that
          // has 358, while Pick a month was still one of its options.
          'maxWidth': '100%',
          '& .MuiToggleButton-root': {
            // The keyboard's answer, and no ring. A segmented cell is reached
            // by keyboard more often than most controls, and the ring drew a
            // second edge round the thumb that is already the selection
            // (2026-11-01). Its label underlines instead, as a tab's does,
            // over the surface answer an unselected cell has.
            [FOCUS_VISIBLE]: STRIP_FOCUS,
            'margin': CELL_INSET,
            'borderColor': 'transparent !important',
            'borderRadius': `${theme.shape.borderRadius}px !important`,
            // A cell has a height, and the one block padding it takes is the
            // contract a button keeps: its word set on its capitals. Heebo's
            // sit `capRise` of an em above the line's middle, so the cell's
            // content drops by the rise (in the cell's own ems, which are the
            // word's), and a glyph beside the word is set back by it, as a
            // button's slots are (below), so it stays on the cell's middle.
            'paddingTop': `${String(2 * typeTokens.families.ui.capRise)}em`,
            'paddingBottom': 0,
            // In pixels from the cell's type size: an em on the glyph would be
            // the glyph's own. Adjust price's Amount and Percentage carry one.
            '& > svg, & > [data-glyph]': {
              marginTop:
                -2 *
                typeTokens.families.ui.capRise *
                typeTokens.console.small.size,
            },
            'fontSize': theme.typography.body2.fontSize,
            // One line, in a cell of one line's height. Squeezed by a field
            // beside it, the Edit price dialog's `$ USD` broke at its space
            // and drew the code under the cell.
            'whiteSpace': 'nowrap',
            'minWidth': 0,
            // Over the thumb, whatever order the two are in. By document order
            // alone, a thumb moved after the cells paints its opaque fill
            // over the word it is meant to sit under.
            'position': 'relative',
            'zIndex': 1,
          },
          // The thumb carries the selection's fill, so a cell gives its own
          // up, at rest and under the pointer. Left on the cell, it would draw
          // the selection twice while the thumb travels, once in place and
          // once arriving, and MUI's selected cell steps its wash on hover, a
          // second and darker one over the thumb.
          '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: 'transparent',
          },
          '& .MuiToggleButton-root.Mui-selected:hover': {
            backgroundColor: 'transparent',
          },
          '& .MuiToggleButton-sizeSmall': {
            height: cellHeight(layout.controlHeightSmall),
            paddingInline: layout.controlInsetSmall,
          },
          '& .MuiToggleButton-sizeMedium': {
            height: cellHeight(layout.controlHeight),
            paddingInline: layout.controlInset,
          },
          '& .MuiToggleButton-sizeLarge': {
            height: cellHeight(layout.controlHeightLarge),
            paddingInline: layout.controlInsetLarge,
          },
          // The selection. It travels on `sheet`, the console's one motion
          // that moves a thing, and takes its new colour on `state` while it
          // goes. Reduced motion keeps the colour and drops the travel, in
          // the one block CssBaseline applies.
          [`& .${SEGMENTED_THUMB_CLASS}`]: {
            'position': 'absolute',
            'top': 0,
            'left': 0,
            'zIndex': 0,
            'borderRadius': theme.shape.borderRadius,
            'pointerEvents': 'none',
            'transition': [
              theme.motion.sheet.transition('transform', 'width', 'height'),
              theme.motion.state.transition('background-color'),
            ].join(', '),
            // Not placed yet, or nowhere to be: hidden, and the next
            // placement lands where it is put instead of travelling there.
            '&:not([data-placed])': {
              visibility: 'hidden',
              transition: 'none',
            },
            // Its fill is the selected cell's, by the colour that cell was
            // asked for, which `SegmentedControl` names on it.
            ...Object.fromEntries(
              SEGMENT_COLORS.map((color) => [
                `&[data-color="${color}"]`,
                { backgroundColor: selectedLook(theme, color).fill },
              ]),
            ),
          },
        },
      },
    },
  }
}
